import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useDroppable } from "@dnd-kit/core";
import _ from "lodash";
import cx from "classnames";

import {
  createExperiencesStoryboardList,
  resetExperiencesStoryboardListCreate,
  updateExperiencesStoryboardList,
  deleteExperiencesStoryboardList,
} from "src/redux/actions/experiences/storyboardLists";
import { ScrollableContainer } from "src/components/scroller";
import { StoryboardListItem } from "src/types/experiences/storyboardLists";
import Slider from "src/components/slider";
import Button from "src/components/button";
import SwitchButton from "src/components/switchButton";
import Text from "src/components/text";

import { StoryboardsContext } from "../../context";

import styles from "./storyboardLists.module.scss";

const DEFAULT_STORYBOARD_LISTS: StoryboardListItem[] = [
  { id: "all", name: "All" },
  { id: "owned_by_me", name: "Owned by me" },
];

interface StoryboardListsProps {
  workspaceId: string;
}

export const StoryboardLists: React.FC<StoryboardListsProps> = ({
  workspaceId,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { listId } = useParams<{ listId: string }>();

  const { editMode } = useContext(StoryboardsContext);
  const renameMode = editMode === "rename";
  const deleteMode = editMode === "delete";
  // The folder more-menu (global toggle) is only offered in normal mode.
  const interactive = !editMode;

  const lists = useSelector((state: any) => state.experiences.storyboardLists.items);
  const loading = useSelector((state: any) => state.experiences.storyboardLists.loading);
  const creating = useSelector((state: any) => state.experiences.storyboardLists.create.creating);
  const created = useSelector((state: any) => state.experiences.storyboardLists.create.created);
  const createdItem = useSelector((state: any) => state.experiences.storyboardLists.create.item);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<StoryboardListItem | null>(null);

  // Navigate to the newly created folder after creation succeeds, then clear the
  // create state so this one-shot redirect can't re-fire when the component
  // remounts later (e.g. returning from Collections) — mirrors collections.
  useEffect(() => {
    if (created && createdItem?.id) {
      setShowCreateForm(false);
      navigate(`/experiences/${workspaceId}/storyboards/folder/${createdItem.id}`);
      dispatch(resetExperiencesStoryboardListCreate());
    }
  }, [created, createdItem]);

  const isActive = (id: string) => {
    if (id === "all") return !listId;
    return listId === id;
  };

  const getTo = (id: string) => {
    if (id === "all") return `/experiences/${workspaceId}/storyboards`;
    return `/experiences/${workspaceId}/storyboards/folder/${id}`;
  };

  const handleSave = (name: string) => {
    dispatch(createExperiencesStoryboardList({ name }));
  };

  const handleDeleted = (deleted: StoryboardListItem) => {
    setDeleteTarget(null);
    // If the folder we just removed is the one being viewed, fall back to All.
    if (isActive(deleted.id)) {
      navigate(`/experiences/${workspaceId}/storyboards`);
    }
  };

  return (
    <div className={styles.lists}>
        {showCreateForm ? (
          <CreateStoryboardListForm
            creating={creating}
            onSave={handleSave}
            onCancel={() => setShowCreateForm(false)}
          />
        ) : (
          <AddStoryboardListButton onClick={() => setShowCreateForm(true)} />
        )}
      <ScrollableContainer distance={300}>
        {DEFAULT_STORYBOARD_LISTS.map((list) => (
          <Link
            key={list.id}
            to={getTo(list.id)}
            className={cx(styles.list, { [styles.active]: isActive(list.id) })}
          >
            <span className={styles.name}>{list.name}</span>
          </Link>
        ))}

        {lists.map((list: StoryboardListItem) => (
          <StoryboardListLink
            key={list.id}
            to={getTo(list.id)}
            list={list}
            active={isActive(list.id)}
            renameMode={renameMode}
            deleteMode={deleteMode}
            interactive={interactive}
            onRequestDelete={() => setDeleteTarget(list)}
          />
        ))}

        {loading &&
          _.times(2).map((i) => (
            <div key={i} className={cx(styles.list, styles.placeholder)}>
              <div className={cx(styles.name, styles.block)} />
            </div>
          ))}
      </ScrollableContainer>

      {deleteTarget && (
        <DeleteStoryboardListDialog
          show={!!deleteTarget}
          list={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

interface StoryboardListLinkProps {
  to: string;
  list: StoryboardListItem;
  active: boolean;
  renameMode: boolean;
  deleteMode: boolean;
  interactive: boolean;
  onRequestDelete: () => void;
}

// Droppable folder link — accepts storyboard cards dragged from the grid.
// - rename mode: the active folder turns into an inline editable form.
// - delete mode: every folder shows a × (collections-style) → confirm panel.
// - normal mode: the active folder shows a more-menu (global toggle).
const StoryboardListLink: React.FC<StoryboardListLinkProps> = ({
  to,
  list,
  active,
  renameMode,
  deleteMode,
  interactive,
  onRequestDelete,
}) => {
  const { renameEdits, onRenameChange } = useContext(StoryboardsContext);

  // Locked folders (canEdit === false) can't be renamed or deleted — they render
  // as plain pills with no inline editor, no × and no more-menu.
  const canModify = list.canEdit !== false;

  const { setNodeRef, isOver } = useDroppable({
    id: list.id,
    disabled: active || renameMode,
    data: {
      list,
      accepts: ["storyboard"],
    },
  });

  // Rename mode: the active folder pill becomes an editable input bound to the
  // staged rename edits; the menubar Save commits it (collections-style).
  if (renameMode && active && canModify) {
    const value = renameEdits[list.id]?.name ?? list.name;
    return (
      <div className={cx(styles.list, styles.active, styles.renaming)}>
        <input
          autoFocus
          className={styles.renameInput}
          value={value}
          onChange={(e) => onRenameChange(list.id, "folder", e.target.value)}
        />
      </div>
    );
  }

  // Let the click bubble to the Link so the folder is navigated to (becomes
  // active) while also opening the confirmation panel.
  const handleDeleteClick = () => {
    onRequestDelete();
  };

  return (
    <Link
      ref={setNodeRef}
      to={to}
      className={cx(styles.list, {
        [styles.active]: active,
        [styles.dropOver]: isOver,
        [styles.locked]: active && !canModify,
      })}
    >
      <span className={styles.name}>{list.name}</span>
      {deleteMode && canModify && (
        <i
          className={cx("paperflite-close", styles.deleteIcon)}
          onClick={handleDeleteClick}
        />
      )}
      {interactive && active && canModify && <FolderMoreMenu list={list} />}
    </Link>
  );
};

interface FolderMoreMenuProps {
  list: StoryboardListItem;
}

// The "⋯" menu shown on the active folder pill: just the Global-folder toggle.
// The menu is portaled to <body> (with fixed positioning) so the horizontal
// scroller's `overflow-y: hidden` doesn't clip it.
const FolderMoreMenu: React.FC<FolderMoreMenuProps> = ({ list }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const iconRef = useRef<HTMLElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (iconRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Keep clicks inside the menu from navigating the parent Link.
  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleToggleClick = (e: React.MouseEvent) => {
    stop(e);
    const rect = iconRef.current?.getBoundingClientRect();
    if (rect) {
      setPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    }
    setOpen((o) => !o);
  };

  const handleGlobalToggle = () => {
    dispatch(
      updateExperiencesStoryboardList({
        id: list.id,
        name: list.name,
        global: !list.global,
      }),
    );
  };

  return (
    <div className={styles.more} onClick={stop}>
      <i
        ref={iconRef}
        className={cx("paperflite-more", styles.moreIcon)}
        onClick={handleToggleClick}
      />
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.moreMenu}
            style={{ position: "fixed", top: pos.top, right: pos.right }}
            onClick={stop}
          >
            <div className={styles.toggleRow}>
              <span>Global folder</span>
              <SwitchButton
                className={styles.globalToggle}
                id={`global-folder-${list.id}`}
                checked={!!list.global}
                handleCheck={handleGlobalToggle}
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

const AddStoryboardListButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button className={styles.addButton} onClick={onClick}>
    <span className={styles.icon}>
      <i className="paperflite-add" />
    </span>
    <span className={styles.name}>New Folder</span>
  </button>
);

interface CreateStoryboardListFormProps {
  creating: boolean;
  onSave: (name: string) => void;
  onCancel: () => void;
}

const CreateStoryboardListForm: React.FC<CreateStoryboardListFormProps> = ({
  creating,
  onSave,
  onCancel,
}) => {
  const [name, setName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && name.trim()) {
      onSave(name.trim());
    }
    if (e.key === "Escape") {
      onCancel();
    }
  };

  return (
    <div className={styles.createForm}>
      <input
        ref={inputRef}
        className={styles.input}
        placeholder="Folder name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={creating}
      />
      <div className={styles.actions}>
        <button
          className={cx(styles.actionBtn, styles.save)}
          onClick={() => onSave(name.trim())}
          disabled={!name.trim() || creating}
        >
          <i className="paperflite-tick" />
        </button>
        <button
          className={cx(styles.actionBtn, styles.cancel)}
          onClick={onCancel}
          disabled={creating}
        >
          <i className="paperflite-close" />
        </button>
      </div>
    </div>
  );
};

interface DeleteStoryboardListDialogProps {
  show: boolean;
  list: StoryboardListItem;
  onClose: () => void;
  onDeleted: (list: StoryboardListItem) => void;
}

const DeleteStoryboardListDialog: React.FC<DeleteStoryboardListDialogProps> = ({
  show,
  list,
  onClose,
  onDeleted,
}) => {
  const dispatch = useDispatch();
  const [submitted, setSubmitted] = useState(false);

  const deleting = useSelector(
    (state: any) => state.experiences.storyboardLists.deletion[list.id]?.deleting ?? false,
  );
  const deleted = useSelector(
    (state: any) => state.experiences.storyboardLists.deletion[list.id]?.deleted ?? false,
  );

  useEffect(() => {
    if (submitted && deleted) {
      onDeleted(list);
    }
  }, [submitted, deleted]);

  const handleConfirm = () => {
    setSubmitted(true);
    dispatch(deleteExperiencesStoryboardList({ id: list.id }));
  };

  return (
    <Slider.Dialog width="300" show={show} onClose={onClose}>
      <Slider.Header
        className={styles.deleteSliderHeader}
        title="deleting folder..."
        message={<Text text={list.name} tooltip={{ placement: "bottom" }} />}
      />
      <Slider.Body>
        <p>Are you sure you want to delete this folder?</p>
        <Text
          text={list.name}
          className={styles.deleteTargetName}
          tooltip={{ placement: "bottom" }}
        />
        <p>
          The storyboards inside won't be deleted — they'll move back to All.
        </p>
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          text={deleting ? "Deleting..." : "Confirm"}
          disabled={deleting}
          onClick={handleConfirm}
        />
        <Button
          variant="secondary"
          text="Cancel"
          disabled={deleting}
          onClick={onClose}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};
